/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import DatePicker from "react-datepicker";
import { PatientsContext } from ".";
import { useParams } from "react-router-dom";
import fetchHelper from "../../services/fetchHelper";
import spinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import produce from "immer";
import { toISODate } from "../../utils/helpers";

const Form = ({ data }) => {
  const { insurance, setInsurance } = useContext(PatientsContext);
  const { i } = useParams();
  const { addToast } = useToasts();
  const [cData, setCData] = useState(data || {});
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const onChange = (e) => {
    const element = e.target;
    const key = element.dataset["key"];
    let value = element.value;
    if (value === "") {
      value = undefined;
    }
    setCData((prev) =>
      produce(prev, (draft) => {
        draft[key] = value;
      })
    );
  };

  useEffect(() => {
    if (insurance.saved) {
      save();
    }
  }, [insurance.saved]);

  const save = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    setInsurance({ saving: true });
    try {
      let d;
      const data = {
        ...cData,
        patientId: i,
        authorizationStr: cData.authorization?.toString(),
        insuranceProfile: {},
      };
      if (cData.id) {
        d = await fetchHelper.put("/patientmanagement/rest/insurance/update/" + cData.id, data);
        setInsurance({
          type: "update-list",
          data: d,
        });
      } else {
        d = await fetchHelper.post("/patientmanagement/rest/insurance/create/", data);
        setInsurance({
          type: "add-list",
          data: d,
        });
      }
      setIsEdit(false);
    } catch (err) {
      handleErrors(err);
    }
    setSaving(false);
    setInsurance({ saving: false });
  };

  const handleErrors = (err) => {
    const errors = err instanceof Object ? err : JSON.parse(err);
    const errArray = errors.validationErrors || errors.errors;
    errArray.forEach((err) => {
      addToast(err.msg || err.errorMessage, {
        appearance: "error",
        autoDismiss: false,
      });
    });
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await fetchHelper.delete("/patientmanagement/rest/insurance/" + cData.id);
      setInsurance({ type: "remove-list", data: cData });
    } catch (err) {
      console.log(err);
    }
    setRemoving(false);
  };

  return (
    <>
      <div className="row rowStretch" style={{ position: "relative" }}>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Insurance Status <sub className="required-field">*</sub>
            </span>
            <select data-key="status" value={cData.status} onChange={onChange}>
              <option value="">Select</option>
              {["ACTIVE", "INACTIVE"].map((item) => (
                <option value={item} key={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Payer Type <sub className="required-field">*</sub>
            </span>
            <select data-key="payerType" value={cData.payerType} onChange={onChange}>
              <option value="">Select</option>
              {["PRIMARY", "SECONDARY", "TERTIARY"].map((item) => (
                <option value={item} key={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Insurance Type</span>
            <select data-key="insuranceType" value={cData.insuranceType} onChange={onChange}>
              <option value="">Select</option>
              {["PRIVATE", "MEDICARE", "HMO", "PPO", "FFS", "CAPITATED", "SELFPAY", "OTHER"].map((item) => (
                <option value={item} key={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Insurance Name <sub className="required-field">*</sub>
            </span>
            <input type="text" data-key="insuranceName" value={cData.insuranceName} onChange={onChange} />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>IPA</span>{" "}
            <input type="text" data-key="policyName" value={cData.policyName} onChange={onChange} />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>
              Policy Number <sub className="required-field">*</sub>
            </span>
            <input type="text" data-key="policyNumber" value={cData.policyNumber} onChange={onChange} />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Group Number</span>{" "}
            <input type="text" data-key="groupNumber" value={cData.groupNumber} onChange={onChange} />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Company ID</span>{" "}
            <input type="text" data-key="companyId" value={cData.companyId} onChange={onChange} />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Subscriber First Name</span>
            <input
              type="text"
              data-key="subscriberFirstname"
              value={cData.subscriberFirstname}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Subscriber Last Name</span>
            <input
              type="text"
              data-key="subscriberLastname"
              value={cData.subscriberLastname}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label className="inCal">
            <span>Subscriber Date of Birth</span>
          </label>
          <DatePicker
            selected={(cData.subscriberDOB && new Date(cData.subscriberDOB.replaceAll("-", "/"))) || ""}
            onChange={(date) => {
              setCData((prev) =>
                produce(prev, (draft) => {
                  draft["subscriberDOB"] = toISODate(date);
                })
              );
            }}
          />
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Subscriber Relation to Patient</span>
            <input
              type="text"
              data-key="subscriberRelation"
              value={cData.subscriberRelation}
              onChange={onChange}
            />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Pre-Auth Required</span>
            <select data-key="authorization" value={cData.authorization} onChange={onChange}>
              <option value="">Select</option>
              {[
                { value: true, label: "YES" },
                { value: false, label: "NO" },
              ].map((item) => (
                <option value={item.value} key={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <img id="loading-spinner-1" src={spinner} style={{ width: 0, height: 0 }} />
        {!isEdit && cData.id ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
              backgroundColor: "#fff",
              opacity: 0.15,
            }}
          />
        ) : null}
      </div>
      {cData.id ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            borderBottom: "1px solid #ccc",
            padding: "10px 0",
            marginBottom: 10,
          }}
        >
          {!isEdit ? (
            <button className="btn btn-sm" onClick={() => setIsEdit(true)}>
              Edit
            </button>
          ) : (
            <button className="btn btn-sm btnTeal" onClick={save}>
              {saving ? (
                <img id="loading-spinner-2" src={spinner} style={{ width: 15, height: 15 }} />
              ) : (
                "Save"
              )}
            </button>
          )}
          {!isEdit ? (
            <button className="btn btn-urgent btn-sm" onClick={remove}>
              {removing ? (
                <img id="loading-spinner-3" src={spinner} style={{ width: 15, height: 15 }} />
              ) : (
                "Remove"
              )}
            </button>
          ) : (
            <button
              className="btn btn-sm"
              onClick={() => {
                setCData(data);
                setIsEdit(false);
              }}
            >
              Cancel
            </button>
          )}
        </div>
      ) : (
        <div style={{ borderBottom: "1px solid #ccc", margin: "10px 0" }} />
      )}
    </>
  );
};

const Insurance = () => {
  const { insurance, setInsurance } = useContext(PatientsContext);
  const { i } = useParams();
  const fetchData = async () => {
    try {
      const list = await fetchHelper.get({
        url: "/patientmanagement/rest/insurance/findByPatientId/" + i,
        // urlDev: "/jsons/insurance.json"
      });
      setInsurance({ list });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  return (
    <div className="tabContent insurance-information">
      <div className="content">
        <div className="grpHdrPg">
          <h3>
            Patient Profile: <span>John Smith</span>
          </h3>
        </div>
        <div className="grpHdr">
          <h3>Insurance Information</h3>
        </div>
        {insurance.openForm ? <Form /> : null}
        {(insurance.list || []).map((data) => (
          <Form data={data} key={data.id} />
        ))}
      </div>
    </div>
  );
};

export default Insurance;
